// Helper to build ticket HTML for non-fiscal reprint
// Aligned with the original printing style observed in Sales.jsx and user screenshots
export function buildNonFiscalTicketHtml(sale, opts = {}) {
  const {
    products = [],
    settingsLogoData = null,
    settingsLeftName = '',
    ticketHeader = '',
    notes: optsNotes,
    emitterIsMonotributo: optsEmitterIsMonotributo
  } = opts;

  const escapeHtml = (unsafe) => {
    if (unsafe == null) return '';
    return String(unsafe)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  };

  const getProductName = (it) => {
    if (!it) return '';
    if (it.name) return it.name;
    const p = products.find((x) => String(x._id) === String(it.productId));
    return p ? p.name : `Producto ${it.productId}`;
  };

  // Auto-detect if emitter is Monotributo based on whether item prices sum up exactly to sale total
  let emitterIsMonotributo = optsEmitterIsMonotributo;
  if (emitterIsMonotributo === undefined) {
    try {
      const sumOfStoredPrices = (sale.items || []).reduce((acc, it) => {
        const qty = Number(it.quantity || it.cantidad || 1);
        const price = Number(it.unitPriceWithDiscount ?? it.precioUnitario ?? it.price ?? it.unitPrice ?? 0);
        return acc + (qty * price);
      }, 0);
      const totalConDescuento = Number(sale.total || 0) + Number(sale.saleDiscount || 0);
      if (Math.abs(sumOfStoredPrices - totalConDescuento) < 2) {
        emitterIsMonotributo = true;
      } else {
        emitterIsMonotributo = false;
      }
    } catch (e) {
      emitterIsMonotributo = false;
    }
  }

  const dateStr = new Date(sale.createdAt || sale.date || Date.now()).toLocaleDateString('es-ES');
  const saleNumber = sale.saleNumber != null && sale.saleNumber !== '' ? sale.saleNumber : (sale._id || Date.now());
  const saleNumberFormatted = isNaN(Number(saleNumber)) ? String(saleNumber).slice(-6) : String(saleNumber).padStart(6, '0');

  let itemsHtml = '';
  (sale.items || []).forEach((it) => {
    const name = escapeHtml(getProductName(it));
    const qty = Number(it.quantity || it.cantidad || 1);

    // Para tickets NO FISCALES, mostrar precios CON IVA incluido
    const priceWithoutIVA = Number(it.unitPriceWithDiscount ?? it.precioUnitario ?? it.price ?? it.unitPrice ?? 0);
    const alicuotaCode = Number(it.alicuotaIVACode ?? it.alicuotaIVA ?? 5);

    // Mapear código de alícuota a porcentaje de IVA (21% por defecto)
    // Si el emisor es Monotributo, el IVA es 0
    const ivaPercent = emitterIsMonotributo ? 0 : (
      alicuotaCode === 3 ? 0 :
        alicuotaCode === 4 ? 10.5 :
          alicuotaCode === 5 ? 21 :
            alicuotaCode === 6 ? 27 :
              alicuotaCode === 8 ? 5 :
                alicuotaCode === 9 ? 2.5 : 21
    );

    const priceWithIVA = priceWithoutIVA * (1 + ivaPercent / 100);
    const subtotal = (qty * priceWithIVA).toFixed(2);

    // Precio original para mostrar descuento por línea si existe
    const original = Number(it.originalPrice ?? it.precioUnitarioOriginal ?? it.price ?? it.unitPrice ?? it.salePrice ?? it.precioUnitario ?? 0);
    const originalWithIVA = original * (1 + ivaPercent / 100);

    itemsHtml += `
      <tr>
        <td style="padding-top: 10px; padding-bottom: 6px; vertical-align: top; font-size: 13px;">${qty}</td>
        <td style="padding-top: 10px; padding-bottom: 6px;">
          <div style="font-size: 14px; font-weight: 800; color: #000; line-height: 1.1; margin-bottom: 6px;">${name}</div>
          <div style="font-size: 11px; color: #000; font-weight: 600; line-height: 1.3;">
            Precio unit.: $${priceWithIVA.toFixed(2)}
          </div>
          ${originalWithIVA > priceWithIVA + 0.1 ? `
            <div style="font-size: 10px; color: #d32f2f; margin-top: 4px; line-height: 1.3;">
              Precio orig.: <span style="text-decoration: line-through;">$${originalWithIVA.toFixed(2)}</span>
            </div>
          ` : ''}
        </td>
        <td style="text-align: right; padding-top: 10px; padding-bottom: 6px; font-size: 15px; font-weight: 800; vertical-align: top;">$${subtotal}</td>
      </tr>
    `;
  });

  const total = Number(sale.total || 0).toFixed(2);
  const headerLogo = settingsLogoData ? `<div style="text-align:center;margin-bottom:8px"><img src="${settingsLogoData}" alt="logo" style="max-width:140px;max-height:60px;object-fit:contain"/></div>` : '';
  const headerName = escapeHtml(settingsLeftName || 'Empresa de Prueba');

  const html = `<!doctype html>
  <html>
    <head>
      <meta charset="utf-8" />
      <title>Ticket-${escapeHtml(String(saleNumber))}</title>
      <style>
        @media print {
          @page { size: 80mm auto; margin: 0; }
          body { margin: 6mm; }
        }
        body { font-family: 'Courier New', Courier, monospace; color: #000; margin: 0; padding: 10px; width: 300px; }
        .ticket-box { border: 2px solid #000; padding: 2px 10px; font-weight: bold; display: inline-block; margin-bottom: 10px; font-size: 16px; }
        .items-table { width: 100%; border-collapse: collapse; margin-top: 10px; border-top: 1px solid #000; }
        .items-table th { border-bottom: 1px solid #000; padding: 5px 0; text-align: left; font-size: 12px; }
        .totals-section { margin-top: 10px; border-top: 1px solid #000; padding-top: 8px; }
      </style>
    </head>
    <body>
      <div style="text-align: center;">
        <div class="ticket-box">TICKET</div>
      </div>

      <div style="display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 15px;">
        <div style="width: 55%; font-size: 18px; font-weight: 900; line-height: 1.1;">${headerLogo}${headerName}</div>
        <div style="width: 43%; text-align: right; font-size: 13px; font-weight: bold;">
          <div>Fecha: ${dateStr}</div>
          <div>Venta N°: ${saleNumberFormatted}</div>
        </div>
      </div>

      <div style="border-top: 1px solid #000; margin: 8px 0;"></div>
      <div style="font-size: 14px; font-weight: 700; margin-bottom: 8px; color: #000;">
        CLIENTE: ${escapeHtml(sale.customerName || sale.clientName || 'Consumidor Final')}
      </div>
      <div style="border-top: 1px solid #000; margin: 8px 0;"></div>

      <table class="items-table">
        <thead>
          <tr>
            <th style="width: 15%;">CANT.</th>
            <th style="width: 55%;">DESCRIPCIÓN</th>
            <th style="width: 30%; text-align: right;">TOTAL</th>
          </tr>
        </thead>
        <tbody>
          ${itemsHtml}
        </tbody>
      </table>

      <div class="totals-section">
        ${sale.saleDiscount && Number(sale.saleDiscount) > 0 ? `
          <div style="display:flex; justify-content:space-between; font-size: 14px; font-weight: bold; color: #d32f2f; margin-bottom: 4px;">
            <div>DESCUENTO:</div>
            <div>-$${Number(sale.saleDiscount).toFixed(2)}</div>
          </div>
        ` : ''}
        <div style="display:flex; justify-content:space-between; font-size: 18px; font-weight: 900;">
          <div>TOTAL:</div>
          <div>$${total}</div>
        </div>
      </div>

      <div style="margin-top: 25px; font-size: 12px; text-align: center; font-weight: bold; border-top: 1px dashed #ccc; padding-top: 10px;">
        ${escapeHtml(ticketHeader || 'Gracias por su compra')}
      </div>

      ${(sale.notes || optsNotes) ? `
        <div style="margin-top: 20px; border-top: 1px solid #000; padding-top: 8px;">
          <div style="font-size: 11px; font-weight: 700; color: #000; margin-bottom: 3px; text-transform: uppercase; letter-spacing: 0.5px;">Nota:</div>
          <div style="font-size: 12px; color: #000; line-height: 1.4; white-space: pre-wrap; font-weight: bold;">${escapeHtml(sale.notes || optsNotes)}</div>
        </div>
      ` : ''}
    </body>
  </html>`;

  return html;
}

export default buildNonFiscalTicketHtml;
